import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Popconfirm, Typography } from 'antd';
import { retryMemberCourseEvaluation } from '@/services/technical';

import { newRetryRequestId } from './FactSyncRetryButton';

export default function CourseEvaluationRetryButton({
  task,
  memberId,
  roomId,
}) {
  const [state, setState] = useState({});
  const busy = useRef(false);
  const request = useRef(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  // 仅为按钮提示；真实资格及最新状态由服务端锁定任务后校验。
  const eligible =
    task.taskStatus === 'failed' &&
    task.updatedAt > 0 &&
    Number.isInteger(task.attemptCount) &&
    Number.isSafeInteger(task.processedVersion) &&
    task.processedVersion >= 0 &&
    Number.isSafeInteger(task.dirtyVersion) &&
    task.dirtyVersion > task.processedVersion &&
    (task.attemptCount >= 5 || task.nextExecuteAt == null);
  const retry = async () => {
    if (busy.current || state.accepted || state.conflict || !eligible) return;
    busy.current = true;
    setState({ loading: true });
    try {
      if (!request.current)
        request.current = {
          memberId,
          roomId,
          expectedDirtyVersion: task.dirtyVersion,
          expectedAttemptCount: task.attemptCount,
          expectedUpdatedAt: task.updatedAt,
          requestId: newRetryRequestId(),
        };
      const response = await retryMemberCourseEvaluation({
        requestData: request.current,
      });
      const d = response?.data;
      if (
        !['00000', '0'].includes(String(response?.code)) ||
        String(d?.memberId) !== String(memberId) ||
        d?.roomId !== roomId ||
        d?.requestId !== request.current.requestId ||
        !['accepted', 'replayed'].includes(d?.outcome)
      )
        throw new Error('Unconfirmed retry');
      if (mounted.current) setState({ accepted: true });
    } catch (error) {
      const status = error?.response?.status;
      if (mounted.current)
        setState({
          conflict: status === 409,
          error:
            {
              401: '请先登录后台',
              403: '无会员档案菜单权限',
              409: '状态已变化或不符合重试条件，请刷新课程状态',
            }[status] || '结果未确认，再次点击将沿用同一请求；也可刷新课程状态',
        });
    } finally {
      busy.current = false;
    }
  };
  if (!eligible)
    return (
      <Typography.Text type="secondary">不符合人工重试条件</Typography.Text>
    );
  if (state.accepted)
    return (
      <Typography.Text>
        重试请求已受理，请刷新课程状态；不代表评估完成
      </Typography.Text>
    );
  return (
    <>
      <Popconfirm
        title="重新入队这节课的失败评估？后续可能更新档案并触发模型调用与费用，不保证重试成功。"
        onConfirm={retry}
        okText="确认重新入队"
        cancelText="取消"
        disabled={state.loading || state.conflict}
      >
        <Button
          size="small"
          loading={state.loading}
          disabled={state.loading || state.conflict}
        >
          重试课程评估
        </Button>
      </Popconfirm>
      {state.error && (
        <Typography.Paragraph type="warning">
          {state.error}
        </Typography.Paragraph>
      )}
    </>
  );
}
CourseEvaluationRetryButton.propTypes = {
  task: PropTypes.object.isRequired,
  memberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  roomId: PropTypes.number.isRequired,
};
